import { __REACT_NATIVE__ } from '@disco/constants/environment'
import api from '@disco/api/server'

/**
 * Checks if the current page is on a shortened URL domain.
 *
 * Identifies if the current hostname is one of the designated short URL domains
 * used by the application (s.localhost, custom-selection.disco.ac, s.disco.ac).
 *
 * @returns True if the current URL is on a shortened domain, false otherwise
 */
export function isShortenedDomainUrl(): boolean {
  // on some domains not found is replaced with a special page
  //
  // Examples:
  //
  //   http://s.localhost:8000/404
  //   https://s.disco.ac/404
  //   https://s.disco.ac/

  const hostname = window.location.hostname
  if (
    // dev
    hostname === 's.localhost' ||
    // staging (yes, custom-selection exists)
    hostname === 'custom-selection.disco.ac' ||
    // prod
    hostname === 's.disco.ac'
  ) {
    return true
  }
  return false
}

export function isShortenedUrl(url: string): boolean {
  // Examples:
  //
  //   http://s.localhost:8000/404
  //   https://s.disco.ac/404
  //   https://s.disco.ac/
  const parsedUrl = new URL(url)
  const hostname = parsedUrl.hostname
  if (
    // dev
    hostname === 's.localhost' ||
    // staging
    hostname === 's.disco-staging-2.com' ||
    // prod
    hostname === 's.disco.ac'
  ) {
    return true
  }
  return false
}

const redirectPaths = ['/cl/', '/explorer/playlists/']

/**
 * Determines if the current URL is an internal redirect URL.
 *
 * Checks if the current pathname starts with any of the designated redirect paths,
 * or if it's a login page with a return URL parameter.
 * Always returns false in React Native environments.
 *
 * @returns True if the current URL is an internal redirect URL, false otherwise
 */
export function isInternalUrlRedirect(): boolean {
  if (__REACT_NATIVE__) return false

  let pathname = window.location.pathname
  if (pathname.startsWith('/login') && window.location.search) {
    pathname = decodeURIComponent(window.location.search)?.replace(
      '?return=',
      '',
    )
  }

  return redirectPaths.some(p => pathname.startsWith(p))
}

export function trimSubdomain(subdomain: string): string {
  // trimSubdomain('s.disco.ac') -> 'disco.ac'
  return subdomain.slice(subdomain.indexOf('.') + 1)
}

function isAllowedHost(hostname: string): boolean {
  const allowedHosts = ['disco.ac', 'disco-staging-2.com', 'localhost']
  if (!allowedHosts.some(host => hostname.includes(host))) {
    return false
  }
  return allowedHosts.some(host => hostname.includes(host))
}

type ShortenUrlResult =
  | { success: true; url: string }
  | {
      success: false
      error: 'INVALID_URL' | 'NETWORK_ERROR' | 'NO_SHORTENED_URL_AVAILABLE'
    }

export async function getDiscoShortenedUrl(
  query: string,
): Promise<ShortenUrlResult> {
  try {
    const initialUrl = new URL(query)
    const originHost = trimSubdomain(initialUrl.hostname)
    const isValidHost = isAllowedHost(initialUrl.hostname)

    if (!isValidHost) {
      return { success: false, error: 'NO_SHORTENED_URL_AVAILABLE' }
    }
    const urlResponse = await api.shorturl([initialUrl.toString()])
    // The response returns an array, get the first valid URL
    const validUrl = urlResponse.find((url: string) => url)

    if (validUrl) {
      const shortenedUrl = new URL(validUrl)
      // Transform  to origin host https://s.disco-staging-2.com/token
      shortenedUrl.protocol = initialUrl.protocol
      shortenedUrl.hostname = `s.${originHost}`
      shortenedUrl.port = ''
      shortenedUrl.pathname = shortenedUrl.pathname.replace(/^\/s\//, '')
      return { success: true, url: shortenedUrl.toString() }
    }

    return { success: false, error: 'NO_SHORTENED_URL_AVAILABLE' }
  } catch (error) {
    console.error('Failed to get disco short URL:', error)

    // Check if it's a URL parsing error
    if (error instanceof TypeError && error.message.includes('Invalid URL')) {
      return { success: false, error: 'INVALID_URL' }
    }

    // Assume network or API error for other cases
    return { success: false, error: 'NETWORK_ERROR' }
  }
}
